import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import AntdRate from 'antd/lib/rate';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import Admin from '../../components/Admin';
import Popover from '../../components/Popover';
import { ReviewLink } from '../../components/Links';

import getRoleBasedPath from '../../helpers/getRoleBasedPath';
import {
  columnCreatedAt,
  columnUpdatedAt,
} from '../../components/common';

const Rate = styled(AntdRate)`
  color: ${({ theme }) => theme.less.primaryColor};

  ${({ disabled }) => disabled && css`
    .ant-rate-star > div:hover, .ant-rate-star > div:focus {
      transform: none;
    }
  `}
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 10px;
  zoom: 0.8;
`;

const Truncate = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;

  @media (min-width: 460px) {
    max-width: 100px;
  }

  @media (min-width: 768px) {
    max-width: 400px;
  }
`;

export const columnOrder = {
  path: 'order',
  title: 'Order',
  render: (record, column, props) => (
    <ReviewLink
      location={props.location}
      pathname={getRoleBasedPath(props, '/reviews')}
      subject={record}
      valuePath="order"
      valueName="order"
    >
      <Link to={`${getRoleBasedPath(props, `/orders/${record.order}`)}`}>
        {record.order}
      </Link>
    </ReviewLink>
  ),
};

export const columnProduct = {
  path: 'product',
  title: 'Product',
  sort: 'product',
  render: (record, column, props) => (
    <ReviewLink
      location={props?.location}
      pathname={getRoleBasedPath(props, '/reviews')}
      subject={record}
    >
      <Link to={`${getRoleBasedPath(props, `/products/${record.product}`)}`}>
        <Truncate>
          {record.productName}
        </Truncate>
      </Link>
    </ReviewLink>
  ),
};

export const columnRating = {
  path: 'rating',
  title: 'Rating',
  sort: 'rating',
  render: (record) => (
    <Rate disabled value={record.rating} />
  ),
};

export const columnMessage = {
  path: 'message',
  title: 'Comment',
  width: '400px',
  render: (record) => (
    <Popover content={(
      <div style={{ maxWidth: '250px' }}>
        {record.message}
      </div>
    )}
    >
      <Truncate>
        {record.message}
      </Truncate>
    </Popover>
  ),
};

export const columnStore = {
  path: 'store',
  title: 'Store',
  render: (record, column, props) => (
    record.store && record.store.name
      ? (
        <ReviewLink
          location={props.location}
          subject={record}
          pathname={getRoleBasedPath(props, '/reviews')}
          valuePath="store"
          valueName="store"
        >
          <Link to={`${getRoleBasedPath(props, `/stores/${record.store}`)}`}>
            {record.store.name}
          </Link>
        </ReviewLink>
      )
      : 'N/A'
  ),
};

export const columnCustomerName = {
  path: 'customer.name',
  title: 'Customer',
  render: (record, column, props) => (
    <ReviewLink
      location={props.location}
      pathname={getRoleBasedPath(props, '/reviews')}
      subject={record}
      valuePath="customer._id"
      valueName="customer"
    >
      {record.customer.name}
    </ReviewLink>
  ),
};

export const columnApproved = {
  path: 'approved',
  title: '',
  width: '20px',
  render: (record) => (
    record.approved
    ? <CheckOutlined />
    : <CloseOutlined style={{ opacity: 0.2 }} />
  ),
};

export const ColumnActions = (props) => {
  const [actionApprove, actionReject] = useMemo(
    () => [true, false].map((approved, i) => (
      Admin.compileFromLibrary(['ActionWithRequest', {
        name: `action-approve-${i}`,
        title: approved ? <CheckOutlined /> : <CloseOutlined />,
        getRequestConfig: (actionProps) => ({
          url: `/reviews/${actionProps.record._id}/approve`,
          method: 'POST',
          data: { approved },
        }),
        disabled: [disabledProps => disabledProps.record.approved === approved],
        ghost: true,
        handleSuccess: (data, actionProps) => actionProps.reload(),
      }]))
    ),
    [],
  );

  return (
    <ActionsWrapper>
      {actionApprove.render(props)}
      {actionReject.render(props)}
    </ActionsWrapper>
  );
}

export const ADMIN = [
  columnApproved,
  columnProduct,
  columnRating,
  columnMessage,
  columnOrder,
  columnCustomerName,
  columnStore,
  columnCreatedAt,
  columnUpdatedAt,
];

export const SELLER = [
  columnApproved,
  columnProduct,
  columnRating,
  columnMessage,
  columnOrder,
  columnCustomerName,
  columnCreatedAt,
  columnUpdatedAt,
];
